import { useEffect, useState } from "react";
import { deliveryClientNBC } from "../../api/cmsDeliveryClient";
import { type ReportLogin } from "../../models/content-types/reportLogin";
import { type ReportDashboard } from "../../models/content-types/report_dashboard";
import { toast } from "react-toastify";
import { getDashboardReport } from "../../api/cventapi";
import { generateReport } from "../../Redux/DashboardAction";
import { AxiosError } from "axios";

export default function Dashboard() {
  const [reportLoginPageLayout, setReportLoginPageData] = useState<ReportLogin | null>(null);
  const [dashboardData, setDashboardPageData] = useState<ReportDashboard | null>(null);
  const [eventCode, setEventCode] = useState("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Get Data for Dashboard Page using Kontent.ai Delivery Client
    deliveryClientNBC
      .items()
      .inFilter("system.type", ["report_dashboard", "report_login"])
      .toPromise()
      .then((response) => {
        const dashboardPageData = response.data.items.find(
          (item) => item.system.type === "report_dashboard"
        ) as ReportDashboard;

        const reportLoginLayoutElement = response.data.items.find(
          (item) => item.system.type === "report_login"
        ) as ReportLogin;

        setDashboardPageData(dashboardPageData);
        setReportLoginPageData(reportLoginLayoutElement);
      });
  }, []);

  // Function to generate report based on event code and report headers
  const handleGenerateReport = async () => {
    setLoading(true);
    const reportHeaders = dashboardData?.elements.report_data_header_text.value || "";

    if (!eventCode.trim()) {
      toast.error("Please enter an Event Code.", {
        position: "top-right",
        autoClose: 3000,
      });
      setLoading(false);
      return;
    }

    if (reportHeaders === "") {
      toast.error("Report Headers Not Found.", {
        position: "top-right",
        autoClose: 3000,
      });
      setLoading(false);
      return;
    }

    try {
      const data = await getDashboardReport(eventCode, reportHeaders);

      console.log("Report API Response:", data); // Debug output

      if (!data) {
        toast.error("No data returned from API.", {
          position: "top-right",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      const { fileBlob, message, fileName } = data;

      if (fileBlob === null) {
        toast.error(message || "No data found or report generation failed.", {
          position: "top-right",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      generateReport(fileBlob, fileName);

      toast.success(message || "Report Generated Successfully", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        toast.error(err.message || "Failed to generate report.", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("An unknown error occurred.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {reportLoginPageLayout && dashboardData && (
        <div id="main-wrapper" className="mb-5">
          <section className="report_header">
            <div className="header-text">
              <div className="row">
                <div className="pt-5">
                  <span className="header-text font-bold font-20">
                    {reportLoginPageLayout.elements.content_header_text.value}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="dashboard_header">
            <div className="dashboard-text">
              <h2 className="border-2 border-bottom border-danger mb-3 font-20 p-2 text-center">
                {dashboardData.elements.report_header_text.value}
              </h2>
            </div>
          </section>

          <section className="dashboard_container">
            <div className="dashboard_items">
              <form>
                <div className="row input-row">
                  <div className="col-md-2">
                    <label className="form-label font-bold">
                      {dashboardData.elements.report_input_label.value}
                    </label>
                  </div>
                  <div className="col-md-4 col-lg-4">
                    <input
                      className="form-control logininput"
                      type="text"
                      placeholder={dashboardData.elements.first_text_box_placeholder.value}
                      onChange={(e) => setEventCode(e.target.value)}
                    />
                  </div>
                  <div className="col">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleGenerateReport}
                      disabled={loading}
                    >
                      {loading ? "Generating..." : dashboardData.elements.button_text.value}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
